import React, { RefObject } from 'react';
import {
  KeyboardTypeOptions,
  Platform,
  StyleSheet,
  TextInput,
  ViewStyle,
} from 'react-native';
import { isDesktopBrowser } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

import DateSlash from './DateSlash';
import { Variants } from './FixedCharacterInput.constants';

interface CharacterInputFieldProps {
  isSelected: boolean;
  errorMessage?: string;
  index: number;
  style?: ViewStyle;
  focusField: (index: number) => void;
  fields: RefObject<(TextInput | null)[]>;
  handleChangeText: (index: number, text: string) => void;
  handleKeyPressTextInput: (index: number, text: string) => void;
  clearInputs: boolean;
  digits: string[];
  keyboardAppearance: 'light' | 'default' | 'dark';
  keyboardType: KeyboardTypeOptions;
  variant: Variants;
  isCustomNumPad?: boolean;
}

const CharacterInputField = ({
  isSelected,
  index,
  errorMessage,
  focusField,
  fields,
  handleChangeText,
  handleKeyPressTextInput,
  clearInputs,
  digits,
  keyboardAppearance,
  keyboardType,
  variant,
  isCustomNumPad,
}: CharacterInputFieldProps) => {
  const selectedStyles = isSelected
    ? {
        ...styles.highlightStyle,
        outlineColor: theme.color.interactive.primaryHovered,
      }
    : {};

  const fullWidthStyle = variant === Variants.Otp ? { flex: 1 } : {};

  const inputStyle = errorMessage?.length
    ? {
        ...styles.input,
        ...fullWidthStyle,
        ...selectedStyles,
        ...styles.errorStyle,
      }
    : { ...styles.input, ...fullWidthStyle, ...selectedStyles };

  return (
    <>
      <TextInput
        autoFocus={index === 0}
        editable={!isCustomNumPad || isDesktopBrowser}
        accessibilityLabel="input"
        underlineColorAndroid="rgba(0,0,0,0)"
        style={inputStyle}
        onFocus={() => focusField(index)}
        ref={(ref: TextInput | null) => {
          if (fields.current) {
            fields.current[index] = ref;
          }
        }}
        onChangeText={(text) => {
          const chars = text.replace(/[^0-9]/g, '');
          if (chars.length > 0) {
            handleChangeText(index, chars);
          } else {
            fields.current?.[index]?.clear();
          }
        }}
        onKeyPress={({ nativeEvent: { key } }) => {
          handleKeyPressTextInput(index, key);
        }}
        testID={'otp-input-' + index}
        value={!clearInputs && digits[index] ? digits[index] : ''}
        keyboardAppearance={keyboardAppearance}
        keyboardType={keyboardType}
        textContentType="oneTimeCode"
        autoCorrect={false}
        autoCapitalize="none"
        autoComplete="off"
      />
      {variant === Variants.Date && index === 1 ? <DateSlash /> : null}
    </>
  );
};

const styles = StyleSheet.create({
  errorStyle: {
    borderColor: theme.color.status.negative,
  },
  highlightStyle: {
    ...theme.typography.title.md,
    borderColor: theme.color.interactive.primaryHovered,
  },
  input: {
    ...theme.typography.title.md,
    borderColor: theme.color.border.primary,
    borderRadius: theme.spacing.xs,
    borderWidth: 1,
    height: 50,
    minWidth: theme.spacing.xxl,
    textAlign: 'center',
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineWidth: 0,
      },
    }),
  },
});

export default CharacterInputField;
